<template>
  <div class="answer-actions">
    <!-- A real <label> wrapping a real <input type="radio">. The visible box is
         the SIBLING span, not the label, so `:focus-visible` on the input can
         style it without `:has()` — which is Safari 15.4+ and this app targets
         iOS 15. -->
    <label
      v-for="control in controls"
      :key="control.key"
      class="answer-actions__btn"
      :data-response="control.response"
      :data-option-index="control.optionIndex ?? undefined"
    >
      <input
        type="radio"
        class="answer-actions__radio"
        :name="groupName"
        :value="control.key"
        :checked="isChecked(control)"
        :disabled="submitting"
        @change="onPick(control)"
      />

      <span
        class="answer-actions__row"
        :class="[
          `answer-actions__row--${control.kind}`,
          { 'answer-actions__row--solo': isSolo(control) },
          { 'answer-actions__row--checked': isChecked(control) },
        ]"
      >
        <!-- The thumbnail slot is reserved on EVERY option as soon as ANY of
             them carries a photo, so the labels stay on one vertical line. An
             option without one gets an EMPTY slot — never a placeholder glyph,
             which would read as "this one has no picture" rather than as
             alignment. `alt=""` because the label beside it already names the
             option: the backend ships no alt text, and repeating the label
             would make a screen reader say it twice. -->
        <span
          v-if="showThumbs && control.option"
          class="answer-actions__thumb"
          :class="{ 'answer-actions__thumb--filled': control.option.photoUrl }"
        >
          <img
            v-if="control.option.photoUrl"
            :src="control.option.photoUrl"
            alt=""
            class="answer-actions__thumb-img"
          />
        </span>

        <span class="answer-actions__main">
          <span class="answer-actions__label" :dir="control.option ? 'auto' : undefined">
            {{ labelFor(control) }}
          </span>
          <!-- The only consequence subline left on any control: NO's money line.
               Everything the three answers used to promise individually is said
               ONCE, card-level, by `answer.advice`. -->
          <small v-if="sublineFor(control)" class="answer-actions__sub">{{
            sublineFor(control)
          }}</small>
        </span>

        <!-- "Your previous answer" — deliberately TEXT inside the label, so it
             joins the radio's accessible name and a screen-reader user hears
             which one is on record with no aria plumbing. It tracks the RECORDED
             answer, not the checked one: the moment she changes her selection
             the two diverge, and pinning it here is what tells her what the
             leader still has. -->
        <span v-if="isOnRecord(control)" class="answer-actions__flag">{{
          t('collection.answer.current')
        }}</span>

        <span v-if="moneyFor(control)" class="answer-actions__money">{{ moneyFor(control) }}</span>
      </span>
    </label>

    <!-- A FOURTH thing she can say: "don't write it off — I know somewhere else
         that has it."

         Deliberately NOT a radio in the group above, and the difference is not
         cosmetic. Those three are SELECTIONS that the card's own submit button
         sends; this one OPENS a sheet and posts from there. Rendering it as a
         fourth radio would let her select it and press submit, which would POST
         a `response` value the backend does not have — a guaranteed 422 on the
         one control that exists to make her feel heard.

         So it is a button, it sits outside the radio group, and it carries a
         chevron plus a subline saying another step follows. It reads as the
         fourth answer without lying about what tapping it does. -->
    <button
      v-if="canSuggestSupplier"
      type="button"
      class="answer-actions__suggest"
      :disabled="submitting"
      @click="emit('suggest-supplier')"
    >
      <span class="answer-actions__main">
        <span class="answer-actions__label">{{ t('collection.answer.suggestSupplier') }}</span>
        <small class="answer-actions__sub">{{ t('collection.answer.suggestSupplierHint') }}</small>
      </span>
      <span class="answer-actions__suggest-chevron" aria-hidden="true">›</span>
    </button>
  </div>
</template>

<script setup>
/**
 * AnswerActions — the answer stack for one question. PRESENTATIONAL ONLY: it
 * reports the CHOICE through `v-model`, and the CARD assembles the payload and
 * sends it. That split is what keeps the 422-sensitive logic (`option_index`
 * required exactly when options exist) in one place instead of two.
 *
 * ── Selecting is not committing ─────────────────────────────────────────────
 * This used to post on tap, which made one input double as the submit button:
 * the optional note sat BELOW the control that committed, so a member had to
 * fill it before tapping and nothing on screen said so. The stack is now a
 * single-select group and the card owns an explicit submit button.
 *
 * Built on real `<input type="radio">` rather than `role="radiogroup"` +
 * `aria-checked` on buttons: native keyboard traversal (arrow keys within the
 * group, one tab stop for the group), native AT semantics, native RTL, and no
 * aria state to keep in sync with a ref.
 *
 * ── Two different "chosen" states, which must never be conflated ────────────
 *   • CHECKED (`modelValue`) — what she is about to send. Rendered as the
 *     control's own selected styling.
 *   • ON RECORD (`currentResponse` / `chosenOptionIndex`) — what the leader
 *     currently has. Rendered as the `collection.answer.current` chip.
 * On a revision the card opens pre-checked on her recorded answer, so the two
 * coincide. The moment she changes the selection they diverge and the chip
 * STAYS on the recorded answer. That is the informative state, not a bug.
 *
 * Two rules decide everything rendered here, and both come from the contract:
 *
 *  1. Render EXACTLY `allowedResponses` — no more, no fewer. The backend's
 *     `ItemIssue::availableResponses()` is the same predicate its write gate
 *     uses, so a response we hide is an answer the member is silently denied,
 *     and one we invent is a 422. `wait` appears only when the leader opted in.
 *  2. YES expands to ONE control per offered option, in contract order — the
 *     option IS the answer, so selecting it both says yes and picks it. A bare
 *     YES is legal ONLY when there are no options; on a question that has any,
 *     every YES must carry an `option_index`, so there is deliberately no
 *     bare-YES control to select. Rendering one would guarantee a 422.
 *
 * Never derives anything from `reason`. That is a reporting tag now — the member
 * reads `question`, and the per-reason response matrix died with the six-case
 * enum.
 *
 * MONEY: renders only what the parent passes, pre-formatted, and never computes
 * a figure. On an already-collected item it renders none at all — see
 * `optionLineImpact` / `declineLineImpact` for why any figure there would be one
 * the backend will not charge.
 */
import { computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  MEMBER_RESPONSE,
  orderedResponses,
  responseOutcomeKey,
} from '@/constants/collectionIssue.js';

const props = defineProps({
  // What she is about to send: `{ response, optionIndex }` or null before she
  // picks anything. NOT what is on record — see the docblock.
  modelValue: {
    type: Object,
    default: null,
  },
  // The leader's offered options — `{ index, label, priceCents, qty, photoUrl }`.
  // Each becomes its own YES control, labelled with the leader's own free text.
  options: {
    type: Array,
    default: () => [],
  },
  // The contract's list, rendered verbatim.
  allowedResponses: {
    type: Array,
    default: () => [],
  },
  // Pre-FORMATTED money strings keyed by OPTION INDEX. The parent owns both the
  // derivation (only a line whose inputs are all knowable gets one) and the
  // currency formatting, so this stays presentational. An option absent from the
  // map simply carries no figure — never a zero.
  optionAmounts: {
    type: Object,
    default: () => ({}),
  },
  // The pre-formatted figure that comes off the order if she declines, or null.
  declineAmount: {
    type: String,
    default: null,
  },
  // Her own recorded pick, so a revision opens on the control she chose.
  chosenOptionIndex: {
    type: Number,
    default: null,
  },
  // Her own recorded answer (null before she answers).
  currentResponse: {
    type: String,
    default: null,
  },
  // A submit is in flight. The ONLY thing that makes the radios inert — a round
  // the server has locked disables SUBMIT, not the selection: she can still look
  // through the options and change what is highlighted while the card explains
  // why it cannot be sent. There is no spinner here any more; it lives on the
  // submit button, which is the control she actually pressed.
  submitting: {
    type: Boolean,
    default: false,
  },
  // The goods are already in her box. Swaps the YES/NO labels for the
  // after-the-fact wording and suppresses every figure on the card.
  isCollected: {
    type: Boolean,
    default: false,
  },
  // Would the backend accept a supplier suggestion on this line right now? The
  // PARENT answers it, because the answer needs both the issue's own status and
  // the item's — see the gate in IssueCard. False hides the fourth control
  // entirely rather than offering a door that 403s.
  canSuggestSupplier: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'suggest-supplier']);

const { t } = useI18n({ useScope: 'global' });

// Radios only form one group when they share a `name`, and the name must not
// collide if two cards ever share a page.
const uid = useId();
const groupName = computed(() => `ci-answer-${uid}`);

const controls = computed(() =>
  orderedResponses(props.allowedResponses, props.options, { isCollected: props.isCollected }),
);

/**
 * Reserve the thumbnail slot on every option as soon as ONE of them has a
 * photo. A mixed list is a normal case per the 2026-08-14 brief — the leader
 * photographs the shapes that need it and names the rest — so alignment has to
 * be decided for the LIST, not per row.
 */
const showThumbs = computed(() => props.options.some((option) => option?.photoUrl));

/** Zero options → the single YES is the filled `--solo` variant. */
function isSolo(control) {
  return control.response === MEMBER_RESPONSE.YES && control.optionIndex == null;
}

function labelFor(control) {
  // An option control is labelled by the leader's OWN words — free text, never
  // an i18n key, and possibly naming something the catalogue never recorded.
  return control.labelKey ? t(control.labelKey) : (control.option?.label ?? '');
}

/**
 * Does this control match a `{ response, optionIndex }` pair? An option answer
 * matches on the INDEX, not just the response — otherwise every YES control on a
 * two-option question would claim to be the same answer.
 *
 * A `yes` with no index matches only a control that has none, so a legacy
 * recorded answer flags nothing rather than guessing the first option.
 */
function matches(control, answer) {
  if (answer == null || control.response !== answer.response) return false;
  if (control.optionIndex == null) return answer.optionIndex == null;
  return control.optionIndex === answer.optionIndex;
}

/** What she is about to send. */
function isChecked(control) {
  return matches(control, props.modelValue);
}

/** What the leader currently has on record. */
function isOnRecord(control) {
  if (props.currentResponse == null) return false;
  return matches(control, {
    response: props.currentResponse,
    optionIndex: props.chosenOptionIndex,
  });
}

/**
 * The figure this control causes, or null. Nothing is derived here — the parent
 * passes pre-formatted strings — and NOTHING at all is rendered on an
 * already-collected item, where every figure would be false: `collect()` returns
 * at its COLLECTED branch before an option is applied, and "the whole line comes
 * off your order" is untrue about goods sitting in her carton.
 */
function moneyFor(control) {
  if (props.isCollected) return null;
  if (control.optionIndex != null) return props.optionAmounts?.[control.optionIndex] ?? null;
  if (control.response === MEMBER_RESPONSE.NO) return props.declineAmount ?? null;
  return null;
}

function sublineFor(control) {
  const amount = moneyFor(control);
  const key = responseOutcomeKey(control.response, { hasAmount: amount != null });
  return key ? t(key, { amount }) : '';
}

/** Selecting posts NOTHING — it only reports what is now highlighted. */
function onPick(control) {
  emit('update:modelValue', { response: control.response, optionIndex: control.optionIndex });
}
</script>

<style scoped>
.answer-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* The label is a bare wrapper; the visible control is __row below. */
.answer-actions__btn {
  display: block;
  cursor: pointer;
}

/* Visually hidden but NOT display:none / visibility:hidden — either would take
   the radio out of the tab order and off the accessibility tree, which is the
   whole reason for using a real input. */
.answer-actions__radio {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

.answer-actions__row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  padding: 10px 14px;
  border: 1.5px solid var(--ci-control-border);
  border-radius: var(--poolit-radius-lg);
  background: var(--ci-surface);
  color: var(--poolit-text-primary);
  font-family: inherit;
  text-align: start;
}

/* The fourth control. Borrows the row's box so it sits in the stack as a peer,
   but is DASHED rather than solid: it belongs with the answers without claiming
   to be one of the three the submit button sends. The chevron says the same
   thing again, for anyone reading shape rather than border style. */
.answer-actions__suggest {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 56px;
  padding: 10px 14px;
  border: 1.5px dashed var(--ci-control-border);
  border-radius: var(--poolit-radius-lg);
  background: var(--ci-surface);
  color: var(--poolit-text-primary);
  font-family: inherit;
  font-size: inherit;
  text-align: start;
  cursor: pointer;
}

.answer-actions__suggest:focus-visible {
  outline: none;
  border-color: var(--poolit-primary-500);
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
}

.answer-actions__suggest:disabled {
  opacity: 0.5;
  cursor: default;
}

/* A plain '›' rotates with the writing direction because the surrounding text
   is bidi — no [dir] rule, and so no reason to reach for :global() (which a
   scoped block cannot express as a compound selector anyway). */
.answer-actions__suggest-chevron {
  flex: none;
  font-size: var(--poolit-font-size-lg);
  line-height: 1;
  color: var(--poolit-text-muted);
}

/* Adjacent sibling, not :has() — see the template comment. */
.answer-actions__radio:focus-visible + .answer-actions__row {
  outline: none;
  border-color: var(--poolit-primary-500);
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
}

.answer-actions__radio:disabled + .answer-actions__row {
  opacity: 0.5;
  cursor: default;
}

/* The option's own picture. Generous on purpose: on a shapes question the
   picture IS what makes the option pickable and the label is the caption. NOT
   independently zoomable — the whole row is one target, and a nested button
   inside a <label> wrapping a radio is invalid markup besides. */
.answer-actions__thumb {
  display: grid;
  place-items: center;
  flex: none;
  width: 76px;
  height: 76px;
  overflow: hidden;
  border-radius: var(--poolit-radius-md);
}

/* Only a slot that HAS a picture gets a surface. An empty slot must be truly
   empty: a filled grey tile is read as a broken image or as "this one has no
   photo", which is exactly the placeholder the spec forbids — the slot exists
   for alignment, and alignment is not something to draw. */
.answer-actions__thumb--filled {
  background: var(--ci-surface-soft);
}

.answer-actions__thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* The label side wraps; the money chip never does — see __money below. */
.answer-actions__main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.answer-actions__label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: var(--poolit-font-size-base);
  font-weight: var(--poolit-font-weight-semibold);
  line-height: 1.3;
}

.answer-actions__sub {
  font-size: var(--poolit-font-size-xs);
  font-weight: var(--poolit-font-weight-regular);
  line-height: 1.4;
  /* text-secondary, not text-muted: muted on --ci-brand-bg measures 4.28:1 and
     fails AA, so text on a tinted control steps up a rung (spec Section 8). */
  color: var(--poolit-text-secondary);
}

/* flex:none so a long option label wraps rather than squeezing the figure, and
   the figure itself never breaks across lines. */
.answer-actions__money {
  flex: none;
  white-space: nowrap;
  font-size: var(--poolit-font-size-md);
  font-weight: var(--poolit-font-weight-bold);
  font-variant-numeric: tabular-nums;
  color: var(--poolit-text-primary);
}

/* An offered option: brand-tinted, brand-edged. */
.answer-actions__row--yes {
  border-color: var(--ci-control-border-brand);
  background: var(--ci-brand-bg);
}

.answer-actions__row--yes .answer-actions__label,
.answer-actions__row--yes .answer-actions__money {
  color: var(--ci-brand-fg);
}

/* The bare YES on a question with no options — the one filled control. Uses
   --ci-brand-fill (primary-700, 7.29:1 with white) and NOT primary-500, which
   measures 4.14:1 and fails AA in both themes (spec Section 4). */
.answer-actions__row--solo {
  border-color: var(--ci-brand-fill);
  background: var(--ci-brand-fill);
}

.answer-actions__row--solo .answer-actions__label,
.answer-actions__row--solo .answer-actions__money {
  color: var(--poolit-text-on-primary);
}

.answer-actions__row--solo .answer-actions__sub {
  color: var(--poolit-text-on-primary);
  opacity: 0.88;
}

/* Declining is legitimate, not punished: the hue names the direction, it is not
   a big red primary. */
.answer-actions__row--no .answer-actions__label,
.answer-actions__row--no .answer-actions__money {
  color: var(--ci-danger-fg);
}

.answer-actions__row--no .answer-actions__money {
  font-size: var(--poolit-font-size-sm);
}

.answer-actions__row--wait .answer-actions__label {
  color: var(--poolit-text-secondary);
}

/* What she is about to send. Deliberately the SAME ring the focus state uses —
   selection and focus coincide the moment she picks with a keyboard, and two
   competing rings on one control read as two different states. */
.answer-actions__row--checked {
  border-width: 2px;
  border-color: var(--poolit-primary-500);
  box-shadow: 0 0 0 3px var(--ci-brand-ring);
}

/* What the leader has on record — independent of the ring above. */
.answer-actions__flag {
  flex: none;
  white-space: nowrap;
  padding: 3px 8px;
  border-radius: var(--poolit-radius-full);
  background: var(--ci-brand-fill);
  color: var(--poolit-text-on-primary);
  font-size: var(--poolit-font-size-2xs);
  font-weight: var(--poolit-font-weight-bold);
  line-height: 1.4;
}
</style>
